import React from 'react';
import { formatFramework, type TeamRepoScore } from '@sdd-maturity/shared-types';
import { scoreColor } from '../lib/score';
import { classifyRepos } from '../lib/utils';

export interface TeamScoreHeaderProps {
  repos: TeamRepoScore[];
}

/** Count distinct frameworks across repos, grouping none/unknown as "No Framework". */
function countDistinctFrameworks(repos: TeamRepoScore[]): number {
  const keys = new Set<string>();
  for (const r of repos) {
    const fw = r.framework;
    if (fw === 'stale') continue;
    // Group by the display label (spec #631, AC-3) so the chart legend shows
    // 'SDD-PDLC' instead of the raw 'pdlc' literal. formatFramework is an
    // identity passthrough for every other value, so grouping is unchanged.
    const key = (!fw || fw === 'none' || fw === 'unknown') ? 'No Framework' : formatFramework(fw);
    keys.add(key);
  }
  return keys.size;
}

/** Aggregate score summary header for a team's repos. */
export function TeamScoreHeader({ repos }: TeamScoreHeaderProps): React.JSX.Element {
  // Exclude skipped/failed repos from score aggregation
  const scoredRepos = repos.filter((r) => r.overallScore >= 0 && r.framework !== 'stale' && r.framework !== 'evaluation-failed' && r.framework !== 'inaccessible');
  const activeRepos = classifyRepos(repos).active;
  const scoredCount = scoredRepos.length;
  const avgScore =
    scoredCount > 0
      ? scoredRepos.reduce((sum, r) => sum + r.overallScore, 0) / scoredCount
      : 0;

  let totalRecommendations = 0;
  for (const r of repos) {
    totalRecommendations += r.recommendationsCount ?? 0;
  }

  const stats = [
    { label: 'Active Repos', value: String(activeRepos.length), color: 'var(--genai-sem-accent)' },
    { label: 'Average Score', value: String(Math.round(avgScore)), color: scoreColor(avgScore) },
    { label: 'Recommendations', value: String(totalRecommendations), color: 'var(--genai-sem-accent)' },
  ];

  return (
    <div data-testid="team-score-header">
      {/* Row 1: 4 equal-width stat cards (AC-3.1); .stat-grid wraps to 2-up < 768px (AC-3.2) */}
      <div className="stat-grid mb-md">
        {stats.map((s) => (
          <div key={s.label} data-testid="stat-card" className="card text-center p-md">
            <p className="text-3xl font-bold leading-tight" style={{ color: s.color }}>{s.value}</p>
            <span className="text-base text-muted">{s.label}</span>
          </div>
        ))}

        {/* 4th card: Framework distribution (AC-2.1) */}
        <div data-testid="stat-card" className="card p-md flex flex-col justify-center">
          <FrameworkDistribution repos={repos} />
        </div>
      </div>
    </div>
  );
}

function FrameworkDistribution({ repos }: { repos: TeamRepoScore[] }): React.JSX.Element {
  const frameworkCount = countDistinctFrameworks(repos);

  return (
    <div data-testid="framework-distribution" className="text-center">
      {frameworkCount > 0 ? (
        <>
          <p className="text-3xl font-bold leading-tight" style={{ color: 'var(--genai-sem-accent)' }}>
            {frameworkCount}
          </p>
          <span className="text-base text-muted">Frameworks</span>
        </>
      ) : (
        <span className="text-base text-muted" style={{ display: 'block' }}>No scan data</span>
      )}
    </div>
  );
}
